/**
 * Account Expired Modal Component - アカウント期限切れモーダル
 */
import React, { useState, useEffect } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';

const AccountExpiredModal: React.FC = () => {
  const { t } = useI18n();
  const { logout, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleAccountExpired = (event: CustomEvent) => {
      setMessage(event.detail.message || t('account.expired.message'));
      setIsOpen(true);
    };

    // Listen for account expired events
    window.addEventListener('accountExpired', handleAccountExpired as EventListener);

    return () => {
      window.removeEventListener('accountExpired', handleAccountExpired as EventListener);
    };
  }, [t]);

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  // BillingSectionと同じ処理を使用してアップグレード
  const handleUpgrade = async () => {
    if (!user) return;

    setUpgrading(true);
    setErrorMessage('');
    
    try {
      const token = await user.getIdToken();
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3201' : '';
      
      // 言語設定を取得
      const currentLanguage = localStorage.getItem('language') || 'ja';
      
      const response = await fetch(`${baseUrl}/api/payments/generate-payment-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          language: currentLanguage,
          type: 'subscription',
          planKey: 'monthly'
        })
      });

      const data = await response.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setErrorMessage(t('account.expired.upgradeError'));
        setUpgrading(false);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      setErrorMessage(t('account.expired.upgradeError'));
      setUpgrading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="modal-container account-expired-modal">
        <div className="modal-header">
          <h2 className="modal-title">{t('account.expired.title')}</h2>
        </div>

        <div className="modal-content">
          <div className="alert alert-warning">
            <p>{message}</p>
            <p>{t('account.expired.upgrade')}</p>
          </div>
          
          {errorMessage && (
            <div className="alert alert-danger mt-3">
              <p>{errorMessage}</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-primary" 
            onClick={handleUpgrade}
            disabled={upgrading}
          >
            {upgrading ? t('common.processing') : t('account.expired.upgradeButton')}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleLogout}
            disabled={upgrading}
          >
            {t('common.logout')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountExpiredModal;